import { useEffect, useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useSearchParams } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { api, unwrapObject } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { PageHeader, StatusPill, StatusTabs } from "@/components/patterns";
import { ErrorState } from "@/components/patterns/States";
import { Switch } from "@/components/ui/switch";
import { dateLocale } from "@/lib/dateTime";

interface Usage {
  budget_inr: number;
  spent_inr: number;
  by_feature: { feature: string; requests: number; failed: number; inr: number; made: number; used: number; dismissed: number }[];
  by_user: { user_id: string | null; name: string; requests: number; inr: number }[];
  settings: { configured: boolean; enabled: boolean; model: string; features: Record<string, boolean>; deal_health_max_per_night: number; digest_hour: number; ask_daily_limit: number; speech_configured: boolean; stt_monthly_budget_inr: number; stt_min_call_sec: number; stt_mode: string };
}

/** Every AI feature, in the order shown, with what it does */
const FEATURE: Record<string, string> = {
  LEAD_ENQUIRY: "Reading new enquiries",
  WHATSAPP_REPLY: "WhatsApp reply suggestions",
  TIDY_NOTES: "Tidying notes",
  EMAIL_DRAFT: "Email drafts",
  DEAL_HEALTH: "Deal health checks",
  DAILY_BRIEF: "My Day brief",
  ASK_CRM: "Ask the CRM",
  AGENT: "Assistant that makes changes",
  TEMPLATE_FROM_SAMPLE: "Designs from a sample",
  CALL_TRANSCRIPT: "Call transcripts",
  CALL_SUMMARY: "Call summaries",
  CALL_COACHING: "Call coaching",
};
const FEATURE_HINT: Record<string, string> = {
  LEAD_ENQUIRY: "Reads each new enquiry and suggests product, budget, timeline and urgency",
  WHATSAPP_REPLY: "Suggests 3 replies in the customer's language while the 24-hour window is open",
  TIDY_NOTES: "Turns quick visit, meeting and call notes into a clean summary",
  EMAIL_DRAFT: "Drafts deal and lead emails, including quote cover emails",
  DEAL_HEALTH: "Checks changed open deals every night; Sales Heads get a morning note",
  DAILY_BRIEF: "The 5 things to start with, at the top of each person's My Day",
  ASK_CRM: "Questions in everyday words answered from the records each person may see",
  AGENT: "Creates leads, contacts, follow-ups and notes on request; every change is shown first and saved only on Confirm. What each role may change is set on the Roles page",
  TEMPLATE_FROM_SAMPLE: "Reads a sample proposal or quote PDF and copies its look (Settings → Proposal templates, Quote design). The file isn't kept; nothing is saved until someone saves it",
  CALL_TRANSCRIPT: "A Transcript button on recorded calls: the call written out as text; runs only when someone presses it",
  CALL_SUMMARY: "A Summarise button: summary, next step and suggested outcome from the transcript",
  CALL_COACHING: "A coaching score and tips with each summary, for the rep and their Sales Head",
};
const inr = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "features", label: "Features" },
  { key: "calls", label: "Call transcripts" },
  { key: "limits", label: "Limits" },
  { key: "key", label: "Service keys" },
];

/**
 * Settings → AI assistant, in tabs: Overview (AI on or off, this month's spend), Features (each feature's switch and use,
 * and the nightly deal check), Limits (company budget and per person) and API key. Everything AI is set here.
 */
export function AiUsagePage() {
  const [data, setData] = useState<Usage | null>(null);
  const [failed, setFailed] = useState<unknown>(null);
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab") || "";
  const tab = TABS.some((t) => t.key === asked) ? asked : "overview";
  const setTab = (t: string) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };
  const load = () =>
    api
      .get("/api/Ai/Usage")
      .then((r) => setData(unwrapObject<Usage>(r)))
      .catch(setFailed);
  useEffect(() => {
    void load();
  }, []);

  if (failed) return <ErrorState title="Couldn't load AI usage" error={failed} onRetry={() => (setFailed(null), void load())} />;
  if (!data) return <FormSkeleton />;
  const s = data.settings;
  const featuresOn = Object.keys(FEATURE).filter((k) => s.features[k]).length;

  return (
    <div className="space-y-4">
      <PageHeader title="AI assistant" />
      <StatusTabs label="AI assistant" active={tab} onChange={setTab} tabs={TABS.map((t) => (t.key === "features" ? { ...t, count: Object.keys(FEATURE).filter((k) => !CALL_FEATURES.includes(k) && s.features[k]).length } : t))} />
      <div role="tabpanel" aria-label={TABS.find((t) => t.key === tab)?.label} className="space-y-4">
        {tab === "overview" && <OverviewTab data={data} featuresOn={featuresOn} onChanged={load} onOpen={setTab} />}
        {tab === "features" && <FeaturesTab data={data} onChanged={load} />}
        {tab === "calls" && <CallsTab data={data} onChanged={load} />}
        {tab === "limits" && <LimitsCard onChanged={load} />}
        {tab === "key" && (
          <>
            <ApiKeyCard onChanged={load} />
            <ApiKeyCard kind="sarvam" onChanged={load} />
          </>
        )}
      </div>
    </div>
  );
}

/** AI on or off for everyone, what it is, and this month's spend at a glance */
function OverviewTab({ data, featuresOn, onChanged, onOpen }: { data: Usage; featuresOn: number; onChanged: () => void; onOpen: (tab: string) => void }) {
  const s = data.settings;
  const pct = data.budget_inr > 0 ? Math.min(100, Math.round((data.spent_inr / data.budget_inr) * 100)) : 0;
  const requests = data.by_feature.reduce((n, f) => n + f.requests, 0);
  const top = data.by_user.slice().sort((a, b) => b.inr - a.inr).slice(0, 5);
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Status
            {!s.configured ? <StatusPill tone="danger">Not set up</StatusPill> : s.enabled ? <StatusPill tone="success">On</StatusPill> : <StatusPill tone="neutral">Off</StatusPill>}
          </CardTitle>
          <CardDescription>
            The AI reads only what it needs to make a suggestion, with phone numbers and emails hidden; nothing changes until a person accepts it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border px-3.5 py-3">
            <div>
              <div className="text-[13px] font-semibold">AI for everyone</div>
              <div className="text-xs text-muted-foreground">
                {s.configured ? "Switch off to stop every AI feature at once; each feature also has its own switch." : "Add the AI service key first, under Service keys."}
              </div>
            </div>
            <OptionSwitch label="AI for everyone" on={s.enabled} field="enabled" onChanged={onChanged} />
          </div>
          {!s.configured && (
            <Button variant="outline" size="sm" onClick={() => onOpen("key")}>
              Add the service key
            </Button>
          )}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between text-[13px]">
              <span>Spent this month</span>
              <span>
                <strong className="font-mono">{inr(data.spent_inr)}</strong> {data.budget_inr > 0 ? <>of {inr(data.budget_inr)}</> : "(no limit)"}
              </span>
            </div>
            {data.budget_inr > 0 && (
              <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Budget used">
                <div className={pct >= 90 ? "h-full bg-destructive" : "h-full bg-primary"} style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Tile label="Times used this month" value={String(requests)} />
            <Tile label="Features on" value={`${featuresOn} of ${Object.keys(FEATURE).length}`} action={() => onOpen("features")} />
            <Tile label="Monthly budget" value={data.budget_inr > 0 ? inr(data.budget_inr) : "No limit"} action={() => onOpen("limits")} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Who used it this month</CardTitle>
          <CardDescription>The top people by spend; "System (automatic)" is the enquiry reader and the nightly deal check.</CardDescription>
        </CardHeader>
        <CardContent>
          {top.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">No AI use this month yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {top.map((u) => (
                <li key={u.user_id || "system"} className="flex items-center justify-between py-2 text-[13px]">
                  <span>{u.name}</span>
                  <span className="text-muted-foreground">
                    {u.requests} time{u.requests === 1 ? "" : "s"} · <span className="font-mono text-foreground">{inr(u.inr)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function Tile({ label, value, action }: { label: string; value: string; action?: () => void }) {
  const body = (
    <>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono text-base font-semibold">{value}</div>
    </>
  );
  return action ? (
    <button type="button" onClick={action} className="rounded-lg bg-muted/60 px-3 py-2 text-left transition-colors hover:bg-muted">
      {body}
    </button>
  ) : (
    <div className="rounded-lg bg-muted/60 px-3 py-2">{body}</div>
  );
}

/** Each feature's switch and this month's use, and the nightly deal check's settings */
/** The call features: they need the call transcript service and have their own tab */
const CALL_FEATURES = ["CALL_TRANSCRIPT", "CALL_SUMMARY", "CALL_COACHING"];

/** Each feature's switch and this month's use */
function FeatureTable({ data, keys, title, description, onChanged }: { data: Usage; keys: string[]; title: string; description: string; onChanged: () => void }) {
  const s = data.settings;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Feature</TableHead>
              <TableHead>On</TableHead>
              <TableHead className="text-right">Times used</TableHead>
              <TableHead className="text-right">Failed</TableHead>
              <TableHead className="text-right">Used</TableHead>
              <TableHead className="text-right">Dismissed</TableHead>
              <TableHead className="text-right">Spent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((key) => {
              const f = data.by_feature.find((x) => x.feature === key);
              return (
                <TableRow key={key}>
                  <TableCell className="min-w-[240px]">
                    <div className="font-medium">{FEATURE[key]}</div>
                    <div className="text-xs text-muted-foreground">{FEATURE_HINT[key]}</div>
                  </TableCell>
                  <TableCell>
                    <FeatureSwitch feature={key} on={!!s.features[key]} onChanged={onChanged} />
                  </TableCell>
                  <TableCell className="text-right font-mono">{f?.requests ?? 0}</TableCell>
                  <TableCell className="text-right font-mono">{f?.failed ?? 0}</TableCell>
                  <TableCell className="text-right font-mono">{f?.made ? `${f.used} (${Math.round((f.used / f.made) * 100)}%)` : "—"}</TableCell>
                  <TableCell className="text-right font-mono">{f?.dismissed ?? 0}</TableCell>
                  <TableCell className="text-right font-mono">{inr(f?.inr ?? 0)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

const OffNote = ({ s }: { s: Usage["settings"] }) =>
  !s.enabled ? <p className="rounded-lg bg-warning-soft px-3.5 py-2.5 text-[13px] text-warning">AI is switched off for everyone (Overview), so none of these run until it is switched on.</p> : null;

/** Each AI feature's switch and use, the nightly deal check and the Ask the CRM cap (the call features have their own tab) */
function FeaturesTab({ data, onChanged }: { data: Usage; onChanged: () => void }) {
  const s = data.settings;
  return (
    <>
      <OffNote s={s} />
      <FeatureTable
        data={data}
        keys={Object.keys(FEATURE).filter((k) => !CALL_FEATURES.includes(k))}
        title="Features"
        description={'Switch each feature on or off for everyone, and see this month\'s use. "Used" counts suggestions people applied or sent (as is or edited); a feature nobody uses isn\'t worth keeping on.'}
        onChanged={onChanged}
      />
      <DealCheckCard settings={s} onChanged={onChanged} />
      <AskLimitCard settings={s} onChanged={onChanged} />
    </>
  );
}

/** Call transcripts, summaries and coaching: their switches, this month's use and their settings */
function CallsTab({ data, onChanged }: { data: Usage; onChanged: () => void }) {
  const s = data.settings;
  return (
    <>
      <OffNote s={s} />
      {!s.speech_configured && (
        <p className="rounded-lg bg-warning-soft px-3.5 py-2.5 text-[13px] text-warning">The call transcript service key isn't added yet (Service keys), so these buttons don't show on calls.</p>
      )}
      <FeatureTable
        data={data}
        keys={CALL_FEATURES}
        title="Call transcripts and summaries"
        description="Buttons on recorded calls. Nothing runs by itself: a call is turned into text only when someone presses Transcript or Summarise on it."
        onChanged={onChanged}
      />
      <SpeechCard settings={s} spent={data.by_feature.find((f) => f.feature === "CALL_TRANSCRIPT")?.inr ?? 0} onChanged={onChanged} />
    </>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${h === 0 ? 12 : h > 12 ? h - 12 : h}:00 ${h < 12 ? "am" : "pm"}` }));

/** How many deals the nightly check looks at, and when Sales Heads get the morning note */
function DealCheckCard({ settings, onChanged }: { settings: Usage["settings"]; onChanged: () => void }) {
  const { success, error } = useToast();
  const [perNight, setPerNight] = useState(String(settings.deal_health_max_per_night));
  const [hour, setHour] = useState(String(settings.digest_hour));
  const [busy, setBusy] = useState(false);
  const changed = perNight !== String(settings.deal_health_max_per_night) || hour !== String(settings.digest_hour);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/api/Ai/Options", { deal_health_max_per_night: Number(perNight), digest_hour: Number(hour) });
      success("Saved", "Deal health check settings updated");
      onChanged();
    } catch (e: any) {
      error("Couldn't save", e.message || "Please check the values");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Deal health check</CardTitle>
        <CardDescription>Open deals that changed are checked at 1:30 am (company time). Sales Heads get one note a day about their team's at-risk deals.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="ai-per-night">Deals checked per night (max)</Label>
            <Input id="ai-per-night" type="number" min={1} max={5000} value={perNight} onChange={(e) => setPerNight(e.target.value)} className="w-40 font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ai-digest-hour">Morning note to Sales Heads at</Label>
            <SearchableSelect id="ai-digest-hour" value={hour} onChange={(e) => setHour(e.target.value)} className="h-9 w-40 rounded-md border border-input bg-background px-2 text-sm">
              {HOURS.map((h) => (
                <option key={h.value} value={h.value}>
                  {h.label}
                </option>
              ))}
            </SearchableSelect>
          </div>
          <Button onClick={save} disabled={busy || !changed}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const STT_MODES = [
  { value: "codemix", label: "Mixed (English words in English, Indian languages in their script)" },
  { value: "transcribe", label: "As spoken, in each language's own script" },
  { value: "translate", label: "All in English (translated)" },
  { value: "translit", label: "All in Roman letters" },
];

/** Call transcripts (Sarvam): monthly budget, shortest call, how the text is written */
function SpeechCard({ settings, spent, onChanged }: { settings: Usage["settings"]; spent: number; onChanged: () => void }) {
  const { success, error } = useToast();
  const [budget, setBudget] = useState(String(settings.stt_monthly_budget_inr));
  const [minSec, setMinSec] = useState(String(settings.stt_min_call_sec));
  const [mode, setMode] = useState(settings.stt_mode);
  const [busy, setBusy] = useState(false);
  const changed = budget !== String(settings.stt_monthly_budget_inr) || minSec !== String(settings.stt_min_call_sec) || mode !== settings.stt_mode;
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/api/Ai/Options", { stt_monthly_budget_inr: Number(budget), stt_min_call_sec: Number(minSec), stt_mode: mode });
      success("Saved", "Call transcript settings updated");
      onChanged();
    } catch (e: any) {
      error("Couldn't save", e.message || "Please check the values");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Call transcript settings</CardTitle>
        <CardDescription>
          The monthly budget, the shortest call that gets the buttons, and how transcripts are written.{" "}
          {settings.speech_configured ? `Spent this month: ₹${spent.toLocaleString("en-IN", { maximumFractionDigits: 2 })}.` : "Add the call transcript service key under Service keys first."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="stt-budget">Budget per month (₹, 0 = none)</Label>
            <Input id="stt-budget" type="number" min={0} value={budget} onChange={(e) => setBudget(e.target.value)} className="w-40 font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="stt-min">Shortest call (seconds)</Label>
            <Input id="stt-min" type="number" min={0} max={600} value={minSec} onChange={(e) => setMinSec(e.target.value)} className="w-32 font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="stt-mode">How the transcript is written</Label>
            <SearchableSelect id="stt-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="h-9 w-72 rounded-md border border-input bg-background px-2 text-sm">
              {STT_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </SearchableSelect>
          </div>
          <Button onClick={save} disabled={busy || !changed}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** How many questions each person may ask Ask the CRM a day (saved reports re-run free) */
function AskLimitCard({ settings, onChanged }: { settings: Usage["settings"]; onChanged: () => void }) {
  const { success, error } = useToast();
  const [perDay, setPerDay] = useState(String(settings.ask_daily_limit));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/api/Ai/Options", { ask_daily_limit: Number(perDay) });
      success("Saved", Number(perDay) ? `Up to ${perDay} questions a person a day` : "No daily cap on questions");
      onChanged();
    } catch (e: any) {
      error("Couldn't save", e.message || "Please check the value");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ask the CRM</CardTitle>
        <CardDescription>This caps how many questions a person can ask in a day. Saved reports run again without the AI, so they don't count. 0 = no cap.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="ai-ask-per-day">Questions per person per day</Label>
            <Input id="ai-ask-per-day" type="number" min={0} max={10000} value={perDay} onChange={(e) => setPerDay(e.target.value)} className="w-40 font-mono" />
          </div>
          <Button onClick={save} disabled={busy || perDay === String(settings.ask_daily_limit)}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** AI as a whole on or off (Super Admins) */
function OptionSwitch({ label, on, field, onChanged }: { label: string; on: boolean; field: "enabled"; onChanged: () => void }) {
  const { success, error } = useToast();
  const [value, setValue] = useState(on);
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(on), [on]);
  const flip = async (next: boolean) => {
    setValue(next);
    setBusy(true);
    try {
      await api.put("/api/Ai/Options", { [field]: next });
      success(next ? "AI switched on" : "AI switched off", next ? "Features that are on can be used now" : "No AI feature runs until it is switched on");
      onChanged();
    } catch (e: any) {
      setValue(!next);
      error("Couldn't change it", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };
  return <Switch checked={value} disabled={busy} onCheckedChange={flip} aria-label={label} />;
}

/** Turns one AI feature on or off for everyone (Super Admins) */
function FeatureSwitch({ feature, on, onChanged }: { feature: string; on: boolean; onChanged: () => void }) {
  const { success, error } = useToast();
  const [value, setValue] = useState(on);
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(on), [on]);
  const flip = async (next: boolean) => {
    setValue(next);
    setBusy(true);
    try {
      await api.put(`/api/Ai/Feature/${feature}`, { on: next });
      success(next ? "Switched on" : "Switched off", FEATURE[feature]);
      onChanged();
    } catch (e: any) {
      setValue(!next);
      error("Couldn't change it", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };
  return <Switch checked={value} disabled={busy} onCheckedChange={flip} aria-label={`${FEATURE[feature]} on`} />;
}

interface KeyStatus {
  source: "settings" | "server" | "none";
  hint: string | null;
  verified_at: string | null;
  updated_on: string | null;
}

/** The Anthropic API key: saved here (encrypted, checked with Anthropic first) or taken from the server's settings */
const KEY_KINDS = {
  anthropic: {
    endpoint: "/api/Ai/Key",
    title: "AI service key",
    about: "Paste the key your technical team gives you. It is checked before saving, kept locked away, and never shown again in full.",
    placeholder: "Paste the key",
    saved: "The key works. AI features can use it now.",
    missing: "No key yet: AI can't be used until one is added.",
    removeWarn: "AI stops working unless your technical team has set up a key for the system.",
  },
  sarvam: {
    endpoint: "/api/Ai/SpeechKey",
    title: "Call transcript service key",
    about: "Paste the key your technical team gives you. It is checked before saving and kept locked away. A call's recording is turned into text only when someone presses Transcript or Summarise.",
    placeholder: "Paste the key",
    saved: "The key works. Calls can be turned into text now.",
    missing: "No key yet: call transcripts and summaries can't be made until one is added.",
    removeWarn: "Call transcripts stop unless your technical team has set up a key for the system.",
  },
} as const;

function ApiKeyCard({ onChanged, kind = "anthropic" }: { onChanged: () => void; kind?: keyof typeof KEY_KINDS }) {
  const k = KEY_KINDS[kind];
  const { success, error } = useToast();
  const confirm = useConfirm();
  const [status, setStatus] = useState<KeyStatus | null>(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get(k.endpoint)
      .then((r) => setStatus(unwrapObject<KeyStatus>(r)))
      .catch(() => undefined);
  }, [k.endpoint]);

  const save = async () => {
    setBusy(true);
    try {
      setStatus(unwrapObject<KeyStatus>(await api.put(k.endpoint, { api_key: key.trim() })));
      setKey("");
      success("Key saved", k.saved);
      onChanged();
    } catch (e: any) {
      error("Key not saved", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    const ok = await confirm({ title: "Remove this key?", message: k.removeWarn, confirmLabel: "Remove", destructive: true });
    if (!ok) return;
    try {
      setStatus(unwrapObject<KeyStatus>(await api.delete(k.endpoint)));
      success("Key removed");
      onChanged();
    } catch (e: any) {
      error("Couldn't remove the key", e.message || "Please try again");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-4" /> {k.title}
        </CardTitle>
        <CardDescription>{k.about}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[13px]">
          {!status ? (
            "Loading…"
          ) : status.source === "settings" ? (
            <>
              In use: key ending <strong className="font-mono">…{status.hint}</strong>, saved here{status.verified_at ? `, checked ${new Date(status.verified_at).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" })}` : ""}.
            </>
          ) : status.source === "server" ? (
            <>
              In use: a key set up by your technical team, ending <strong className="font-mono">…{status.hint}</strong>. A key saved here takes its place.
            </>
          ) : (
            <span className="text-destructive">{k.missing}</span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={status?.source === "settings" ? "Paste a new key to replace it" : k.placeholder}
            className="h-9 min-w-[260px] flex-1 font-mono text-[13px]"
            aria-label={k.title}
          />
          <Button size="sm" disabled={busy || !key.trim()} onClick={save}>
            {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
            Save and test
          </Button>
          {status?.source === "settings" && (
            <Button size="sm" variant="outline" onClick={remove}>
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface Limits {
  company_budget_inr: number;
  company_spent_inr: number;
  default_user_limit_inr: number;
  users: { user_id: string; name: string; role: string | null; spent_inr: number; requests: number; mode: "default" | "none" | "custom"; limit_inr: number | null }[];
}

/** The company's monthly budget, the default limit per person, and each person's own limit */
function LimitsCard({ onChanged }: { onChanged: () => void }) {
  const { success, error } = useToast();
  const [data, setData] = useState<Limits | null>(null);
  const [company, setCompany] = useState("");
  const [perPerson, setPerPerson] = useState("");
  const [saving, setSaving] = useState(false);
  const take = (d: Limits) => {
    setData(d);
    setCompany(String(d.company_budget_inr));
    setPerPerson(String(d.default_user_limit_inr));
  };
  const load = () =>
    api
      .get("/api/Ai/Limits")
      .then((r) => take(unwrapObject<Limits>(r)))
      .catch(() => undefined);
  useEffect(() => {
    void load();
  }, []);

  const saveTotals = async () => {
    setSaving(true);
    try {
      take(unwrapObject<Limits>(await api.put("/api/Ai/Limits", { company_budget_inr: Number(company) || 0, default_user_limit_inr: Number(perPerson) || 0 })));
      success("Limits saved");
      onChanged();
    } catch (e: any) {
      error("Limits not saved", e.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const changed = Number(company) !== data.company_budget_inr || Number(perPerson) !== data.default_user_limit_inr;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly limits</CardTitle>
        <CardDescription>
          AI stops for everyone when the company budget is used up, and for a person when their own limit is. Limits start again on the 1st of each month. 0 means no limit. Leads read automatically when they arrive count only against the company budget.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1 text-[13px]">
            <span className="block text-muted-foreground">Company budget per month (₹)</span>
            <Input type="number" min={0} step={100} value={company} onChange={(e) => setCompany(e.target.value)} className="h-9 w-44 font-mono" />
          </label>
          <label className="space-y-1 text-[13px]">
            <span className="block text-muted-foreground">Default limit per person per month (₹)</span>
            <Input type="number" min={0} step={50} value={perPerson} onChange={(e) => setPerPerson(e.target.value)} className="h-9 w-44 font-mono" />
          </label>
          <Button size="sm" disabled={saving || !changed} onClick={saveTotals}>
            {saving && <Loader2 className="mr-1.5 size-4 animate-spin" />}
            Save
          </Button>
          <span className="text-[13px] text-muted-foreground">
            Used this month: <strong className="font-mono text-foreground">{inr(data.company_spent_inr)}</strong>
            {data.company_budget_inr > 0 ? ` of ${inr(data.company_budget_inr)}` : ""}
          </span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead className="text-right">Times used</TableHead>
                <TableHead className="text-right">Used this month</TableHead>
                <TableHead>Limit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((u) => (
                <UserLimitRow key={u.user_id} row={u} defaultLimit={data.default_user_limit_inr} onSaved={load} />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function UserLimitRow({ row, defaultLimit, onSaved }: { row: Limits["users"][number]; defaultLimit: number; onSaved: () => void }) {
  const { success, error } = useToast();
  const [mode, setMode] = useState(row.mode);
  const [amount, setAmount] = useState(row.mode === "custom" && row.limit_inr !== null ? String(row.limit_inr) : "");
  const [busy, setBusy] = useState(false);
  const dirty = mode !== row.mode || (mode === "custom" && Number(amount) !== row.limit_inr);
  const pct = row.limit_inr ? Math.min(100, Math.round((row.spent_inr / row.limit_inr) * 100)) : 0;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/api/Ai/Limits/User/${row.user_id}`, { mode, limit_inr: mode === "custom" ? Number(amount) : undefined });
      success("Limit saved", row.name);
      onSaved();
    } catch (e: any) {
      error("Limit not saved", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <TableRow>
      <TableCell>
        <span className="font-medium">{row.name}</span>
        {row.role && <span className="block text-[11.5px] text-muted-foreground">{row.role}</span>}
      </TableCell>
      <TableCell className="text-right font-mono">{row.requests}</TableCell>
      <TableCell className="text-right">
        <span className="font-mono">{inr(row.spent_inr)}</span>
        {row.limit_inr !== null && (
          <span className="mt-1 ml-auto block h-1.5 w-24 overflow-hidden rounded-full bg-muted" aria-label={`${pct}% of the limit used`}>
            <span className={pct >= 100 ? "block h-full bg-destructive" : "block h-full bg-primary"} style={{ width: `${pct}%` }} />
          </span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-2">
          <SearchableSelect value={mode} onChange={(e) => setMode(e.target.value as any)} className="h-8 w-48 rounded-md border border-input bg-background px-2 text-sm" aria-label={`${row.name}'s limit`}>
            <option value="default">{defaultLimit > 0 ? `Default (${inr(defaultLimit)})` : "Default (no limit)"}</option>
            <option value="none">No limit</option>
            <option value="custom">Own amount</option>
          </SearchableSelect>
          {mode === "custom" && <Input type="number" min={0} step={50} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="₹ per month" className="h-8 w-28 font-mono" aria-label={`${row.name}'s amount`} />}
          {dirty && (
            <Button size="sm" variant="outline" className="h-8" disabled={busy || (mode === "custom" && amount === "")} onClick={save}>
              Save
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
